import { HttpStatusCode } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { LoadingService } from 'src/app/services/loading.service';
import { ReferenceStore } from 'src/app/stores/reference.store';

import { DialogService } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import {
  SVGIcon,
  checkIcon,
  warningTriangleIcon,
  xIcon,
} from '@progress/kendo-svg-icons';
import {
  Observable,
  combineLatest,
  debounceTime,
  finalize,
  of,
  startWith,
  takeUntil,
  tap
} from 'rxjs';
import { CommonService } from 'src/app/services/common.service';
import { RoutesConstantService } from 'src/app/services/routes-constant.service';
import { StudentService } from 'src/app/services/student.service';
import { AbstractForm } from 'src/app/shared/components/abstract-form';
import {
  getReferenceDataById,
  getReferenceDataByValue,
} from 'src/app/shared/functions/get-reference-data-by-id';
import { ApiResponse } from 'src/app/shared/interfaces/api-response';
import {
  EditMsiGuardianResponse,
  NgsisErrorCode,
} from 'src/app/shared/interfaces/edit-msi-guardian-response';
import { IReferenceData } from 'src/app/shared/interfaces/form/reference-data.model';
import { ISedmsFormModel } from 'src/app/shared/interfaces/form/sedms-form-model';
import { GeoClientResponse } from 'src/app/shared/interfaces/goat-address';
import { ParentsInfo } from 'src/app/shared/interfaces/student';
import { StudentProfile } from 'src/app/shared/interfaces/student-profile';
import { EditContactInfoFormModel } from 'src/app/shared/models/edit-student-profile-model/edit-contact-info.model';
import { EditContactResponseModel } from 'src/app/shared/models/edit-student-profile-model/edit-contact-response.model';
import { EditContactSubmitModel } from 'src/app/shared/models/edit-student-profile-model/edit-contact-submit.model';
import { User } from 'src/app/shared/models/user';
import { validateNameEdit, validatePhoneNumberMaskedTextBox } from 'src/app/shared/validators/validator';
import { AuthStore } from 'src/app/stores/auth.store';
import {
  DEBOUNCE_TIME,
  OUT_OF_NEW_YORK_CITY,
  OUT_OF_NEW_YORK_STATE,
} from '../../../shared/constants/edit-student-profile';
import { SCHEDULE_MEETING_CONSTANTS } from '../schedule-meeting/schedule-meeting-constants';

@Component({
  selector: 'app-edit-contact-information',
  templateUrl: './edit-contact-information.component.html',
  styleUrls: ['./edit-contact-information.component.scss'],
  standalone: false,
})
export class EditContactInformationComponent
  extends AbstractForm<
    EditContactResponseModel,
    EditContactResponseModel,
    Record<string, IReferenceData[]>
  >
  implements OnInit {
  @Output() closeEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Output() saveEmitter: EventEmitter<void> = new EventEmitter<void>();
  @Input() studentDetails: StudentProfile;
  @Input() contact: ParentsInfo[];

  warningTriangleSvg: SVGIcon = warningTriangleIcon;
  checkMarkIcon: SVGIcon = checkIcon;
  xMarkIcon: SVGIcon = xIcon;
  timePickerValue: Date = SCHEDULE_MEETING_CONSTANTS.timePickerDefaultValue;
  showPreviousAttemptsBtn: boolean;
  dropDownValues: IReferenceData[];
  loadSpinner: boolean;
  loadSpinners: boolean;
  showToPhone: boolean;
  showToEmail: boolean;
  showToPostal: boolean;
  showFromEmail: boolean;
  showFromPhone: boolean;
  showFromPostal: boolean;
  dateTimeOccured: string;
  canTogleSelected: boolean = true;
  studentRelatedEvent: string;
  studentRelatedEventFormJson: string;

  iepFormSk: string;
  finalizeShown: boolean = true;
  form: FormGroup;
  formModel: ISedmsFormModel = EditContactInfoFormModel;
  showOtherInput: boolean;
  locked: boolean = false;
  userDetails: User;
  authCodeNoRedAlert: IReferenceData[] = [];

  @ViewChild('template', { read: TemplateRef })
  public notificationTemplate: TemplateRef<unknown>;
  parentErrors: { name: string; errors: string[] }[] = [];

  readonly debounceTime: number = DEBOUNCE_TIME;
  readonly emptyStringLength: number = 0;
  readonly defaultStateId: number = 33; // New York State ID
  readonly firstIndex: number = 0;
  constructor(
    private fb: FormBuilder,
    public authStore: AuthStore,
    notificationService: NotificationService,
    private studentService: StudentService,
    private referenceStore: ReferenceStore,
    dialogService: DialogService,
    cdRef: ChangeDetectorRef,
    route: ActivatedRoute,
    formBuilder: FormBuilder,
    public activatedRoute: ActivatedRoute,
    public commonService: CommonService,
    loadingService: LoadingService,
    private routesContantService: RoutesConstantService,
    private router: Router
  ) {
    super(loadingService, cdRef, route, formBuilder, dialogService, notificationService);
    this.authStore?.UserInfo$?.pipe(takeUntil(this.componentDestroyed)).subscribe((userInfo: User) => {
      this.userDetails = userInfo;
    });
    this.loadSpinner = true;
  }

  get entity$(): Observable<EditContactResponseModel> {
    const obj: EditContactResponseModel = new EditContactResponseModel(
      this.studentDetails?.parentDetails[this.firstIndex],
      this.referenceData
    );
    return of(obj);
  }

  // For Getting Data from Dropdown
  get referenceData$(): Observable<IReferenceData[][]> {
    return combineLatest([
      this.referenceStore.state$,
      this.referenceStore.language$,
      this.referenceStore.borough$,
      this.referenceStore.relationship$,
      this.referenceStore.redAlert$,
    ]);
  }

  customFaIcon(icon: IconDefinition): SVGIcon {
    return {
      name: icon.iconName,

      content: `<path d="${icon.icon[4]}" />`,

      viewBox: `0 0 ${icon.icon[0]} ${icon.icon[1]}`,
    };
  }

  newAttendees(data: ParentsInfo): FormGroup {
    return this.fb.group({
      isPrimary: [data.isPrimary],
      residesWithStudent: [data?.residesWithStudent],
      adultId: [data.adultId],
      relationship: [
        getReferenceDataById(
          data.relationship,
          this.referenceData.relationship,
        ),
      ],
      authorizationCode: [
        getReferenceDataByValue(
          data?.authorizationCode,
          this.referenceData.redAlert,
        ),
      ],
      firstName: [data.firstName, validateNameEdit(true)],
      lastName: [data.lastName, validateNameEdit(true)],
      homePhone: [data?.homePhone, validatePhoneNumberMaskedTextBox(false)],
      workPhone: [data?.workPhone, validatePhoneNumberMaskedTextBox(false)],
      cellPhone: [data?.cellPhone, validatePhoneNumberMaskedTextBox(false)],
      streetNumber: [data?.address?.streetNumber, [Validators.required]],
      streetName: [data?.address?.streetName, [Validators.required]],
      apartmentNo: [data?.address?.apartmentNo],
      city: [data?.address?.city, [Validators.required]],
      borough: [
        getReferenceDataById(
          Number(data?.address?.borough),
          this.referenceData.borough,
        ),
        [Validators.required],
      ],
      state: [
        getReferenceDataById(data?.address?.state, this.referenceData.state),
        [Validators.required],
      ],
      zip: [data?.address?.zipCode, [Validators.required]],
      email: [data?.emailAddress],
      spokenLanguage: [
        getReferenceDataById(
          data?.preferredSpokenLang ?? 55,
          this.referenceData.language
        ),
      ],
      writtenLanguage: [
        getReferenceDataById(
          data?.preferredWrittenLang ?? 55,
          this.referenceData.language
        ),
      ],
      ngGuardianID: [data?.ngGuardianID],
      redAlert: [data?.redAlert],
      studentSk: [this.studentDetails?.studentSk],
    });
  }

  addAttendees(): void {
    const control: FormArray = this.form.controls.attendees as FormArray;
    this.studentDetails?.parentDetails?.forEach((item: ParentsInfo) => {
      control.push(this.newAttendees(item));
    });
    this.loadSpinner = false;
  }

  getAttendeesControls(): AbstractControl[] {
    return (this.form.get('attendees') as FormArray).controls;
  }

  initEntity(
    entityData: unknown,
    referenceData: Record<string, IReferenceData[]>,
  ): EditContactResponseModel {
    return new EditContactResponseModel(
      this.studentDetails?.parentDetails[this.firstIndex],
      referenceData,
    );
  }

  initReferenceData([state, language, borough, relationship, redAlert]: [
    IReferenceData[],
    IReferenceData[],
    IReferenceData[],
    IReferenceData[],
    IReferenceData[],
    IReferenceData[],
    IReferenceData[],
  ]): Record<string, IReferenceData[]> {
    relationship = relationship
      .filter(
        (type: IReferenceData) =>
          type?.value !== null && type?.value !== 'NULL',
      )
      .sort((a: IReferenceData, b: IReferenceData) =>
        a.value.localeCompare(b.value),
      );
    this.authCodeNoRedAlert = redAlert.filter((redAlert: IReferenceData) => redAlert?.id !== 2);
    return { state, language, borough, relationship, redAlert };
  }

  override readonly isDialog: boolean = true;

  public splitBeforeAndAfterFirstSpace(input: string): {
    before: string;
    after: string;
  } {
    const [before, ...rest]: string[] = input.split(' ');
    const after: string = rest.join(' ');
    return { before, after };
  }

  saveDetails(): Observable<ApiResponse<EditMsiGuardianResponse[]>> {
    this.loadSpinner = true;
    const request: EditContactSubmitModel[] = [];
    const attendeesArray: FormArray = this.form.get('attendees') as FormArray;

    attendeesArray.controls.forEach((control: AbstractControl) => {
      request.push(
        new EditContactSubmitModel(
          { attendee: control.value },
          this.referenceData
        )
      );
    });
    // Clear the errors array before making any call
    this.parentErrors = [];

    return this.studentService.editGuardianInfo(request).pipe(
      tap((response: ApiResponse<EditMsiGuardianResponse[]>) => {
        let responseIsSuccessful: boolean = true;
        response.value.forEach((res: EditMsiGuardianResponse) => {
          // Errors returned from MSI
          if (res.statusCode !== HttpStatusCode.Ok) {
            const errors: string[] = JSON.parse(
              res.responseBody,
            ).ngsisErrorCodes.map((err: NgsisErrorCode) => err.Value);

            if (responseIsSuccessful) {
              responseIsSuccessful = false;
            }

            this.parentErrors.push({
              name: res.name,
              errors: errors,
            });
          }
        });

        // Display error notification if there are any errors in the response
        if (this.parentErrors.length > 0) {
          return;
        } else {
          // if the response is successful, close the dialog
          this.closeAfterSave();
        }
      }),
      finalize(() => {
        this.loadSpinner = false;
      }),
    );
  }

  initWatches(): void {
    this.addAttendees();

    const attendeesArray: FormArray = this.form.get('attendees') as FormArray;

    // Each attendee group needs its own subscription to monitor changes
    attendeesArray.controls.forEach((attendeeGroup: AbstractControl) => {
      const boroughControl: AbstractControl = attendeeGroup.get('borough');
      const streetNumberControl: AbstractControl =
        attendeeGroup.get('streetNumber');
      const streetNameControl: AbstractControl =
        attendeeGroup.get('streetName');
      const cityControl: AbstractControl = attendeeGroup.get('city');
      const zipControl: AbstractControl = attendeeGroup.get('zip');

      if (boroughControl && streetNumberControl && streetNameControl) {
        combineLatest([
          boroughControl.valueChanges.pipe(startWith(boroughControl.value)),
          streetNumberControl.valueChanges.pipe(
            startWith(streetNumberControl.value),
          ),
          streetNameControl.valueChanges.pipe(startWith(streetNameControl.value)),
        ])
          .pipe(
            debounceTime(this.debounceTime),
            takeUntil(this.componentDestroyed),
          )
          .subscribe(
            ([borough, streetNumber, streetName]: [
              IReferenceData,
              string,
              string,
            ]) => {
              if (
                borough &&
                streetNumber &&
                streetName &&
                (borough.id as number) !== OUT_OF_NEW_YORK_CITY &&
                (borough.id as number) !== OUT_OF_NEW_YORK_STATE
              ) {
                this.commonService
                  .getGOATAddress(borough.description, streetNumber, streetName)
                  .pipe(takeUntil(this.componentDestroyed))
                  .subscribe((response: GeoClientResponse) => {
                    if (!response?.address?.message) {
                      cityControl?.setValue(
                        response?.address?.uspsPreferredCityName?.trim() ?? '',
                      );
                      cityControl?.updateValueAndValidity();

                      zipControl?.setValue(
                        response?.address?.zipCode?.trim() ?? '',
                      );
                      zipControl?.updateValueAndValidity();

                      const stateControl: AbstractControl =
                        attendeeGroup.get('state');
                      stateControl?.setValue(
                        this.referenceData.state.find(
                          (data: IReferenceData) =>
                            data.id === this.defaultStateId,
                        ),
                      );
                      stateControl?.updateValueAndValidity();
                    }
                  });
              }
            },
          );
      }
    });
  }

  public override otherInitalizations(): void {
    // otherInitalizations
  }

  public override afterOnDestroy(): void {
    // afterOnDestroy
  }

  postCancelConfirmation(): void {
    this.loadSpinners = false;
  }

  postSubmitConfirmation(): void {
    // postSubmitConfirmation
  }

  // Called when the user clicks on the (x) icon in the dialog
  closeDailog(): void {
    this.closeEmitter.next();
  }

  // Close the dialog after the user saved parent information
  closeAfterSave(): void {
    this.saveEmitter.next();
  }

  get canSaveDraft(): boolean {
    return !this.form.valid;
  }

  get canSubmit(): boolean {
    return !this.form.valid;
  }

  getIcon(isPrimaryOrResidesWithStudent: boolean): SVGIcon {
    if (isPrimaryOrResidesWithStudent) {
      return this.checkMarkIcon;
    } else {
      return this.xMarkIcon;
    }
  }

  getClassName(isPrimaryOrResidesWithStudent: boolean): string {
    if (isPrimaryOrResidesWithStudent) {
      return 'checkMark';
    } else {
      return 'xMark';
    }
  }

  getAuthCodeData(): IReferenceData[] {
    if (this.form.get('attendees')?.value
      .filter((attendee: ParentsInfo) => !attendee?.redAlert && (attendee?.authorizationCode as unknown as IReferenceData)?.id !== 2)
      .length > 1) {
      return this.referenceData?.redAlert;
    }
    return this.authCodeNoRedAlert;
  }
}
